import React, { useCallback, useEffect, useRef, useState } from "react";

import { Box, Button, Stack, TextareaAutosize, Typography } from "@mui/material";
import {
  HtmlEditor,
  Inject,
  RichTextEditorComponent,
  Toolbar,
  ToolbarSettingsModel,
  ToolbarType
} from "@syncfusion/ej2-react-richtexteditor";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { useActionHook, useRoleGuardHook } from "hooks";
import {
  useDealActions,
  useDealFormDealIdSelect,
  useDealFormDescriptionSelect,
  useDealFormIdSelect,
  usePickUserPermissions
} from "store";
import { UserPermissionType } from "store/types";

import { RoleName } from "../../../constants";

const commonBoxStyles = {
  borderRadius: "3px",
  padding: "25px",
  boxShadow: "0 4px 6px -1px rgba(18, 18, 23, 0.08), 0 2px 4px -1px rgba(18, 18, 23, 0.06)",
  backgroundColor: "#fff",
  width: "100%",
  margin: "10px 0 10px 0"
};

const toolbarBaseItems: ToolbarSettingsModel["items"] = [
  "Bold",
  "Italic",
  "Underline",
  "Undo",
  "Redo",
  "Indent",
  "Outdent",
  "justifyFull",
  "justifyLeft",
  "justifyCenter",
  "justifyRight",
  "clearFormat",
  "UnorderedList",
  "OrderedList"
];

export const DescriptionEditor: React.FC = () => {
  const dealId: number | undefined = useDealFormDealIdSelect();
  const formId: number | undefined = useDealFormIdSelect();
  const formDescription: string | null = useDealFormDescriptionSelect();
  const myDeals = usePickUserPermissions("deals");
  const noAccessPermission = myDeals && myDeals === UserPermissionType.NO_ACCESS;
  const isViewerPermission = myDeals && myDeals === UserPermissionType.VIEWER;

  const { updateFormAction } = useDealActions();

  const [value, setValue] = useState<string>(formDescription || "");
  const [RTLmode, setRTLmode] = useState<boolean>(false);
  const [activeEditor, setActiveEditor] = useState<boolean>(false);
  const rteRef = useRef<any>(null);

  const isAvailable = useRoleGuardHook(
    true,
    [RoleName.Admin, RoleName.Analyst],
    noAccessPermission || isViewerPermission
  );

  const { action: updateDescription, loading } = useActionHook(updateFormAction);

  const getValue = useCallback((data: { name: string; value: string }) => {
    setValue(data.value);
  }, []);

  const changeRTLMode = () => {
    setRTLmode(true);
  };

  const changeLTRMode = () => {
    setRTLmode(false);
  };

  const patchFormDescription = async (value: string, RTLmode: boolean) => {
    if (!formId) return;
    let descriptionValue = "";
    if (RTLmode) {
      // eslint-disable-next-line quotes
      descriptionValue = value.replace(/<(\w+)>/g, '<$1 dir="rtl">');
    } else {
      descriptionValue = value;
    }
    await updateDescription({
      formId: formId,
      dealId: dealId,
      description: descriptionValue
    });
    setActiveEditor(false);
  };

  const toolbarSettings: ToolbarSettingsModel = {
    items: [
      ...toolbarBaseItems,
      {
        template:
          // eslint-disable-next-line quotes
          '<button class="e-tbar-btn e-control e-btn e-lib e-icon-btn"><span class="e-btn-icon e-icons custom-rtl"></span></button>',
        tooltipText: "RTL Mode",
        click: changeRTLMode
      },
      {
        template:
          // eslint-disable-next-line quotes
          '<button class="e-tbar-btn e-control e-btn e-lib e-icon-btn"><span class="e-btn-icon e-icons custom-ltr"></span></button>',
        tooltipText: "LTR Mode",
        click: changeLTRMode
      }
    ],
    type: ToolbarType.Expand
  };

  useEffect(() => {
    setValue(formDescription || "");
  }, [formDescription]);

  useEffect(() => {
    activeEditor && rteRef?.current?.addEventListener("change", getValue);
  }, [activeEditor]);

  return (
    <AsyncContentMui isLoading={loading} spinnerProps={{ size: 75 }}>
      {activeEditor && (
        <Box sx={{ ...commonBoxStyles, marginBottom: "20px" }}>
          <Typography sx={{ color: "#0a3142", fontSize: "15px", marginBottom: "10px", fontWeight: "600" }}>
            Description
          </Typography>
          <Box sx={{ border: "2px solid #dae0e2", marginBottom: "10px" }}>
            <RichTextEditorComponent
              ref={rteRef}
              placeholder="Type your text here"
              undoRedoTimer={300}
              enableRtl={RTLmode}
              cssClass="rich-text-editor"
              toolbarSettings={toolbarSettings}
              height="auto"
              value={value}
              style={{ lineHeight: "15px" }}
              created={() => {
                if (rteRef.current) {
                  const contentArea = rteRef.current.getContent().getElementsByClassName("e-content")[0];
                  if (contentArea) {
                    contentArea.setAttribute("dir", "auto");
                  }
                }
              }}
            >
              <Inject services={[HtmlEditor, Toolbar]} />
            </RichTextEditorComponent>
          </Box>
          <Stack direction="row" justifyContent="flex-end">
            <Button
              onClick={() => {
                formDescription && setValue(formDescription || "");
                setActiveEditor(false);
              }}
              sx={{ marginRight: "21px" }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button onClick={() => patchFormDescription(value, RTLmode)} variant="contained">
              Save
            </Button>
          </Stack>
        </Box>
      )}
      {!value && isAvailable && !activeEditor && (
        <Box sx={{ ...commonBoxStyles, marginBottom: "20px" }}>
          <Typography sx={{ color: "#0a3142", fontSize: "15px", marginBottom: "10px", fontWeight: "600" }}>
            Description
          </Typography>
          <TextareaAutosize
            onClick={() => setActiveEditor(true)}
            style={{
              resize: "none",
              overflow: "hidden",
              cursor: "pointer"
            }}
            className={"text-input text-input__answer text-input--deep-green text-input--underline"}
            placeholder={"Type your description here"}
          />
        </Box>
      )}
      {value && !activeEditor && (
        <Box sx={{ ...commonBoxStyles }}>
          <RichTextEditorComponent
            onClick={() => isAvailable && setActiveEditor(true)}
            className="rich-editor"
            value={value}
            style={{ lineHeight: "15px", ...(isAvailable && { cursor: "pointer" }) }}
            readonly
          >
            <Inject services={[HtmlEditor]} />
          </RichTextEditorComponent>
        </Box>
      )}
    </AsyncContentMui>
  );
};
